<template>
  <div class="video-test-page">
    <h2 class="page-title mb-4">Video Streaming Test</h2>

    <b-card class="mb-4">
      <h5 class="fw-semi-bold">Test Private Video Playback</h5>
      <p class="text-muted">Enter a video key to test CloudFront signed URL generation and playback.</p>

      <b-form @submit.prevent="fetchVideo">
        <b-row>
          <b-col md="8">
            <b-form-group label="Video Key" label-for="videoKey">
              <b-form-input
                id="videoKey"
                v-model="videoKey"
                placeholder="e.g., private/tutorials/intro.mp4"
                :state="videoKeyState"
              ></b-form-input>
              <b-form-invalid-feedback>
                Key must start with 'private/' or 'public/'
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Video Type">
              <b-form-radio-group
                v-model="videoType"
                :options="videoTypeOptions"
                buttons
                button-variant="outline-primary"
              ></b-form-radio-group>
            </b-form-group>
          </b-col>
        </b-row>

        <b-button type="submit" variant="primary" :disabled="loading || !isValidKey">
          <b-spinner small v-if="loading"></b-spinner>
          {{ loading ? 'Fetching...' : 'Fetch Video URL' }}
        </b-button>
      </b-form>
    </b-card>

    <!-- Error Display -->
    <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null">
      <strong>Error:</strong> {{ error }}
    </b-alert>

    <!-- Success Display -->
    <b-card v-if="videoUrl" class="mb-4">
      <h5 class="fw-semi-bold text-success">Video URL Generated</h5>

      <b-form-group label="Signed URL" v-if="expiresIn">
        <b-input-group>
          <b-form-input :value="videoUrl" readonly></b-form-input>
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="copyUrl">
              <i class="fa fa-copy"></i> Copy
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <small class="text-muted">Expires in {{ expiresIn }} seconds ({{ expiresInMinutes }} minutes)</small>
      </b-form-group>

      <b-form-group label="Direct URL" v-else>
        <b-input-group>
          <b-form-input :value="videoUrl" readonly></b-form-input>
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="copyUrl">
              <i class="fa fa-copy"></i> Copy
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <small class="text-muted">Public URL - no expiration</small>
      </b-form-group>
    </b-card>

    <!-- Video Player -->
    <b-card v-if="videoUrl" class="mb-4">
      <h5 class="fw-semi-bold">Video Player</h5>
      <div class="video-container">
        <video
          ref="videoPlayer"
          :src="videoUrl"
          controls
          class="video-player"
          @error="handleVideoError"
          @loadeddata="handleVideoLoaded"
        >
          Your browser does not support the video tag.
        </video>
      </div>
      <div v-if="videoLoaded" class="mt-2">
        <b-badge variant="success">Video loaded successfully</b-badge>
      </div>
      <div v-if="videoError" class="mt-2">
        <b-badge variant="danger">Failed to load video: {{ videoError }}</b-badge>
      </div>
    </b-card>

    <!-- Quick Test Buttons -->
    <b-card class="mb-4">
      <h5 class="fw-semi-bold">Quick Tests</h5>
      <p class="text-muted">Use these buttons to quickly test common scenarios:</p>
      <b-button-group>
        <b-button variant="outline-primary" @click="testPrivateVideo">
          Test Private Video
        </b-button>
        <b-button variant="outline-secondary" @click="testPublicVideo">
          Test Public Video
        </b-button>
      </b-button-group>
    </b-card>
  </div>
</template>

<script>
import videoService from '@/services/videoService';

export default {
  name: 'VideoTest',

  data() {
    return {
      videoKey: 'private/tutorials/test.mp4',
      videoType: 'private',
      videoTypeOptions: [
        { text: 'Private', value: 'private' },
        { text: 'Public', value: 'public' }
      ],
      videoUrl: null,
      expiresIn: null,
      loading: false,
      error: null,
      videoLoaded: false,
      videoError: null
    };
  },

  computed: {
    isValidKey() {
      if (!this.videoKey) return false;
      if (this.videoType === 'private') {
        return this.videoKey.startsWith('private/');
      }
      return this.videoKey.startsWith('public/');
    },

    videoKeyState() {
      if (!this.videoKey) return null;
      return this.isValidKey;
    },

    expiresInMinutes() {
      return this.expiresIn ? Math.round(this.expiresIn / 60) : 0;
    }
  },

  watch: {
    videoType(newType) {
      // Update key prefix when type changes
      if (newType === 'private' && this.videoKey.startsWith('public/')) {
        this.videoKey = this.videoKey.replace('public/', 'private/');
      } else if (newType === 'public' && this.videoKey.startsWith('private/')) {
        this.videoKey = this.videoKey.replace('private/', 'public/');
      }
    }
  },

  methods: {
    async fetchVideo() {
      this.loading = true;
      this.error = null;
      this.videoUrl = null;
      this.expiresIn = null;
      this.videoLoaded = false;
      this.videoError = null;

      try {
        let response;
        if (this.videoType === 'private') {
          response = await videoService.getSignedUrl(this.videoKey);
        } else {
          response = await videoService.getPublicUrl(this.videoKey);
        }

        if (response.success) {
          this.videoUrl = response.url;
          this.expiresIn = response.expiresIn || null;
        } else {
          this.error = response.message || 'Failed to fetch video URL';
        }
      } catch (err) {
        console.error('Error fetching video URL:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to fetch video URL';
      } finally {
        this.loading = false;
      }
    },

    copyUrl() {
      navigator.clipboard.writeText(this.videoUrl).then(() => {
        this.$bvToast.toast('URL copied to clipboard', {
          title: 'Copied',
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        });
      });
    },

    handleVideoError(event) {
      const video = event.target;
      const error = video.error;
      if (error) {
        switch (error.code) {
          case 1:
            this.videoError = 'Video loading aborted';
            break;
          case 2:
            this.videoError = 'Network error while loading video';
            break;
          case 3:
            this.videoError = 'Video decoding failed';
            break;
          case 4:
            this.videoError = 'Video format not supported or access denied';
            break;
          default:
            this.videoError = 'Unknown error';
        }
      }
    },

    handleVideoLoaded() {
      this.videoLoaded = true;
      this.videoError = null;
    },

    testPrivateVideo() {
      this.videoType = 'private';
      this.videoKey = 'private/tutorials/test.mp4';
      this.fetchVideo();
    },

    testPublicVideo() {
      this.videoType = 'public';
      this.videoKey = 'public/marketing/demo.mp4';
      this.fetchVideo();
    }
  }
};
</script>

<style scoped>
.video-test-page {
  padding: 20px;
  max-width: 1200px;
}

.video-container {
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.video-player {
  width: 100%;
  max-height: 500px;
  display: block;
}

.page-title {
  color: #333;
  font-weight: 600;
}
</style>
